/**
 * The regime registry, read (P414, Track J1).
 *
 * `useRegimes()` is the registry; `useRegime()` is the one the BANK is
 * under, resolved from its profile.
 *
 * THE FALLBACK RULE, which matters more than the hook. A bank that has
 * not recorded its regime has not recorded it, and every surface that
 * can say so must -- guessing "sama" because that is where the product
 * started is the invention this codebase refuses everywhere else. So
 * `useRegime()` returns `null` for an unrecorded profile and the caller
 * decides; `SAMA_FALLBACK_CODE` exists only for the narrow case where a
 * LABEL is structurally required and there is nothing to label it with,
 * and it is named so that its use is visible in a diff.
 */
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { useOperatorReads, useOperatorReadsState } from "shared/auth/useOperatorReads";

import { api, type ApiError } from "shared/api/client";
import { useBankIdentity } from "shared/api/hooks/useBankIdentity";
import { regimeKeys } from "shared/api/queryClient";
import type { Regime, RegimeList } from "shared/api/types";

/** Where the product started, and the ONLY place that is written down. */
export const SAMA_FALLBACK_CODE = "sama";

export function useRegimes(enabled = true): UseQueryResult<RegimeList, ApiError> {
  // Round 16 H30 -- after /v1/me, and never for an examiner.
  const allowed = useOperatorReads();
  return useQuery({
    queryKey: regimeKeys.all,
    queryFn: () => api.get<RegimeList>("/v1/regimes"),
    // Reference data: it changes on a migration, not on a workday.
    staleTime: 60 * 60 * 1000,
    // ROUND 13 G9 -- and not before there is a token to ask with. On
    // the auth STATUS, not on reading the token: see useBankIdentity.
    enabled: enabled && allowed,
  });
}

/**
 * The regime this bank is under, or null when it has not said.
 *
 * Returns `undefined` while either query is in flight, so a caller can
 * hold rather than render a label it is about to replace -- the flash
 * of the wrong regulator's word being exactly what P415 is for.
 */
export function useRegime(): {
  regime: Regime | null | undefined;
  isLoading: boolean;
} {
  /* G9 -- the bank IDENTITY, not the admin-only profile. Reading the
     regime code from an admin route meant every other seat was
     refused it and fell back to "no regime recorded", which is the
     one answer this hook exists to avoid guessing. */
  const profile = useBankIdentity();
  const regimes = useRegimes();
  // Not asked yet is not "none recorded": hold, as for a query in
  // flight, until /v1/me has said who is looking.
  const reads = useOperatorReadsState();

  if (reads === "pending" || profile.isLoading || regimes.isLoading) {
    return { regime: undefined, isLoading: true };
  }
  const code = profile.data?.regime_code ?? null;
  if (!code) return { regime: null, isLoading: false };
  const found = regimes.data?.regimes.find((r) => r.code === code) ?? null;
  return { regime: found, isLoading: false };
}
